<template>
  <modal :open="internalOpen" :width="1200">
    <template #header>
      <modal-header
        variant="cross"
        @cancel="closeModal()"
      >
        <h1>{{ $t('pages.licenseList.modal.title') }}</h1>
      </modal-header>
    </template>

    <div class="mt-8">
      <div
        v-if="selectedSeat"
        class="mb-4 flex justify-center"
      >
        <vue-qrcode
          :value="selectedSeat?.qrCode"
          :options="{
            errorCorrectionLevel: 'H',
            width: 300,
          }"
          @ready="onReady"
        />
        <ul class="flex flex-col gap-y-4 justify-center w-fit">
          <li>
            <custom-button variant="primary" @click="downloadQR('svg')">
              {{ $t('pages.licenseList.modal.downloadSVGButton') }}
            </custom-button>
          </li>
          <li>
            <custom-button variant="primary" @click="downloadQR('png')">
              {{ $t('pages.licenseList.modal.downloadPNGButton') }}
            </custom-button>
          </li>
        </ul>
      </div>
      <ul class="flex flex-col gap-y-6">
        <li v-for="license in currentLicenses" :key="license.id">
          <p class="text-body-regular-bold text-table-text-gray uppercase">
            {{ `${$t('licenses.license')} ${license.id}` }}
          </p>
          <p class="mt-2 text-body-regular text-table-text-gray">
            {{ license?.startDate ? format(new Date(license.startDate), 'dd.MM.yyyy') : '-' }}
            -
            {{ license?.endDate ? format(new Date(license.endDate), 'dd.MM.yyyy') : '-' }}
          </p>
          <ul class="ml-8 flex flex-col mt-2 gap-y-4 list-disc">
            <li v-for="seat in license.licenseSeats" :key="seat.id">
              {{ generateSeatLabel(seat) }}
              <download-qr-code-button :seat="seat" />
              <CustomButton
                class="ml-2"
                variant="primary"
                @click="showQrCode(seat)"
              >
                {{ $t('pages.licenseList.modal.showButton') }}
              </CustomButton>
            </li>
          </ul>
        </li>
      </ul>
    </div>
  </modal>
</template>

<script lang="ts" setup>
import { computed, ref, onMounted } from 'vue';
import Modal from '@utils/Modal/Modal.vue';
import ModalHeader from '@utils/Modal/ModalHeader.vue';
import { useVModel } from '@vueuse/core';
import { useI18n } from 'vue-i18n';
import { format } from 'date-fns';
import VueQrcode from '@chenfengyuan/vue-qrcode';
import type { Company, License, LicenseSeat } from '@/entities/Entities';
import DownloadQrCodeButton from './DownloadQrCodeButton.vue';
import CustomButton from '@/components/utils/CustomButton.vue';

interface Props {
  open: boolean;
  company?: Company;
  license?: License;
}

const { t } = useI18n();

const emit = defineEmits(['update:open']);
const props = defineProps<Props>();
const internalOpen = useVModel(props, 'open', emit);

function closeModal() {
  internalOpen.value = false;
}

const currentLicenses = computed(() => {
  if (props.license) {
    return [props.license];
  }
  const licenses = props.company?.licenses ?? [];
  return licenses.filter((license) => {
    if (!license.endDate) return false;
    const today = new Date();
    return today < new Date(license.endDate); // All licenses that haven't ended yet, i.e. currently active and follow-up licenses
  });
});

const generateSeatLabel = (seat: LicenseSeat) => {
  let result = t('licenses.seat');
  if (seat?.type) {
    result += ` (${t('licenses.boxType.DEFAULT')}:`;
    result += ` ${t(`licenses.boxType.${seat.type}`)})`;
  }
  return result;
};

const selectedSeat = ref<LicenseSeat>();
const qrCanvas = ref<HTMLCanvasElement>();

const showQrCode = (seat: LicenseSeat) => {
  selectedSeat.value = seat;
};

function onReady(canvas: HTMLCanvasElement) {
  qrCanvas.value = canvas;
}

function downloadQR(extension: 'svg' | 'png') {
  if (!qrCanvas.value) return;

  const link = document.createElement('a');
  if (extension === 'png') {
    link.href = qrCanvas.value.toDataURL('image/png');
    link.download = `${selectedSeat.value?.company?.name}-${selectedSeat.value?.type}-qr-code.png`;
    link.download = link.download.replaceAll(/[^a-zA-Z0-9\-]/g, '');
  } else {
    const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    const image = document.createElementNS('http://www.w3.org/2000/svg', 'image');
    svg.setAttribute('width', qrCanvas.value.width.toString());
    svg.setAttribute('height', qrCanvas.value.height.toString());
    image.setAttribute('width', '100%');
    image.setAttribute('height', '100%');
    image.setAttribute('href', qrCanvas.value.toDataURL('image/png'));
    svg.appendChild(image);
    const svgData = new XMLSerializer().serializeToString(svg);
    const svgBlob = new Blob([svgData], { type: 'image/svg+xml;charset=utf-8' });
    link.href = URL.createObjectURL(svgBlob);
    link.download = `${selectedSeat.value?.company?.name}-${selectedSeat.value?.type}-qr-code.svg`;
    link.download = link.download.replaceAll(/[^a-zA-Z0-9\-]/g, '');
  }
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}
</script>

<style lang="scss" scoped></style>
